import { useState, useRef, useCallback } from 'react';

const SERVICES = [
  { id: 'residential', icon: 'ri-home-3-line', label: 'Residential Demolition' },
  { id: 'commercial', icon: 'ri-building-4-line', label: 'Commercial / Industrial Demolition' },
  { id: 'arborist', icon: 'ri-plant-line', label: 'Consultation on Arborist Report' },
  { id: 'impact', icon: 'ri-file-chart-line', label: 'Consultation on Impact Assessment Report' },
  { id: 'land', icon: 'ri-map-2-line', label: 'Consultation on Land Assessment Report' },
  { id: 'soil', icon: 'ri-test-tube-line', label: 'Consultation on Soil Test / Geotech Report' },
];

const STEPS = ['Services', 'Contact', 'Address', 'Photos', 'Submit'];

interface FormData {
  services: string[];
  fullName: string;
  phone: string;
  email: string;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
}

const INITIAL_FORM: FormData = {
  services: [],
  fullName: '',
  phone: '',
  email: '',
  address: '',
  suburb: '',
  state: '',
  postcode: '',
};

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

export default function MultiStepForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleService = (label: string) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(label)
        ? prev.services.filter((s) => s !== label)
        : [...prev.services, label],
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    setFiles((prev) => {
      const combined = [...prev, ...dropped];
      return combined.slice(0, 10);
    });
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files).filter((f) => f.type.startsWith('image/'));
      setFiles((prev) => {
        const combined = [...prev, ...selected];
        return combined.slice(0, 10);
      });
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canNext = () => {
    if (step === 0) return form.services.length > 0;
    if (step === 1) return form.fullName.trim() && form.phone.trim() && form.email.trim();
    if (step === 2) return form.address.trim() && form.suburb.trim() && form.state && form.postcode.trim();
    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      body.append('services', form.services.join(', '));
      body.append('name', form.fullName);
      body.append('phone', form.phone);
      body.append('email', form.email);
      body.append('address', `${form.address}, ${form.suburb} ${form.state} ${form.postcode}`);
      body.append('photos', files.length > 0 ? `${files.length} photo(s) uploaded (Uncollectable)` : 'No photos uploaded');

      const response = await fetch('https://readdy.ai/api/form/d6q6ilba90d3mf3egcg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-grey-border p-12 text-center max-w-lg mx-auto">
        <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
          <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
        </div>
        <h3 className="font-heading font-bold text-navy text-2xl mb-3">Request Submitted!</h3>
        <p className="font-sans text-gray-500 text-sm leading-relaxed mb-6">
          Thanks <strong>{form.fullName}</strong>! We've received your quote request and our team will be in touch within a few hours.
        </p>
        <div className="bg-grey-bg rounded-xl p-4 text-left space-y-2 mb-6">
          <p className="text-xs font-sans text-gray-500 uppercase tracking-wider font-semibold mb-2">Summary</p>
          <p className="text-sm font-sans text-navy"><span className="text-gray-400">Services:</span> {form.services.join(', ')}</p>
          <p className="text-sm font-sans text-navy"><span className="text-gray-400">Address:</span> {form.address}, {form.suburb} {form.state} {form.postcode}</p>
          <p className="text-sm font-sans text-navy"><span className="text-gray-400">Contact:</span> {form.email}</p>
        </div>
        <button
          onClick={() => { setSubmitted(false); setStep(0); setForm(INITIAL_FORM); setFiles([]); }}
          className="px-6 py-2.5 bg-navy text-white font-heading font-semibold text-sm rounded-lg hover:bg-navy-light transition-all cursor-pointer whitespace-nowrap"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="multistep-quote-form"
      onSubmit={(e) => e.preventDefault()}
      className="bg-white rounded-2xl shadow-xl border border-grey-border overflow-hidden"
    >
      {/* Progress Header */}
      <div className="bg-navy px-8 py-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white text-lg">Request a Quote</h3>
          <span className="text-xs font-sans text-white/50">Step {step + 1} of {STEPS.length}</span>
        </div>
        <div className="flex items-center space-x-1">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold transition-all ${
                    i < step
                      ? 'bg-orange-brand text-white'
                      : i === step
                      ? 'bg-white text-navy'
                      : 'bg-white/20 text-white/40'
                  }`}
                >
                  {i < step ? <i className="ri-check-line text-xs"></i> : i + 1}
                </div>
                <span className={`text-xs font-sans mt-1 hidden sm:block ${i === step ? 'text-white' : 'text-white/40'}`}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-1 rounded transition-all ${i < step ? 'bg-orange-brand' : 'bg-white/20'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-8">
        {/* Step 0: Services */}
        {step === 0 && (
          <div>
            <h4 className="font-heading font-bold text-navy text-base mb-1">Select Services</h4>
            <p className="font-sans text-gray-400 text-sm mb-5">Choose all that apply to your project.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICES.map((svc) => {
                const selected = form.services.includes(svc.label);
                return (
                  <button
                    key={svc.id}
                    type="button"
                    onClick={() => toggleService(svc.label)}
                    className={`relative flex flex-col items-center text-center p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      selected
                        ? 'border-orange-brand bg-orange-light shadow-sm'
                        : 'border-grey-border bg-grey-bg hover:border-orange-brand/50 hover:bg-orange-light/50'
                    }`}
                  >
                    {selected && (
                      <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center bg-orange-brand rounded-full">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-2 ${selected ? 'bg-orange-brand' : 'bg-navy-50'}`}>
                      <i className={`${svc.icon} text-xl ${selected ? 'text-white' : 'text-navy'}`}></i>
                    </div>
                    <span className={`font-sans text-xs font-semibold leading-tight ${selected ? 'text-navy' : 'text-gray-600'}`}>
                      {svc.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {form.services.length > 0 && (
              <p className="text-xs font-sans text-orange-brand mt-3 font-semibold">
                {form.services.length} service{form.services.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        )}

        {/* Step 1: Contact */}
        {step === 1 && (
          <div>
            <h4 className="font-heading font-bold text-navy text-base mb-1">Your Contact Details</h4>
            <p className="font-sans text-gray-400 text-sm mb-5">We'll use these to send your quote.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="e.g. James Robertson"
                  required
                  className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="e.g. 04xx xxx xxx"
                  required
                  className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="e.g. james@example.com"
                  required
                  className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <div>
            <h4 className="font-heading font-bold text-navy text-base mb-1">Property Address</h4>
            <p className="font-sans text-gray-400 text-sm mb-5">Where is the demolition or assessment required?</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Address Line <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="e.g. 42 Collins Street"
                  required
                  className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Suburb <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="suburb"
                  value={form.suburb}
                  onChange={handleChange}
                  placeholder="e.g. Melbourne"
                  required
                  className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy bg-white focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand appearance-none cursor-pointer transition-colors"
                    >
                      <option value="">Select...</option>
                      {AU_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-sans font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="postcode"
                    value={form.postcode}
                    onChange={handleChange}
                    placeholder="e.g. 3000"
                    maxLength={4}
                    required
                    className="w-full px-4 py-3 border border-grey-border rounded-lg text-sm font-sans text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-brand/30 focus:border-orange-brand transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div>
            <h4 className="font-heading font-bold text-navy text-base mb-1">Site Photos</h4>
            <p className="font-sans text-gray-400 text-sm mb-5">Upload up to 10 photos of the site. This helps us provide a more accurate quote.</p>
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-orange-brand bg-orange-light'
                  : 'border-grey-border hover:border-orange-brand/60 hover:bg-orange-light/40'
              }`}
            >
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-navy-50 rounded-xl">
                <i className="ri-upload-cloud-2-line text-3xl text-navy"></i>
              </div>
              <p className="font-sans text-sm text-gray-600 mb-1">
                <span className="text-orange-brand font-semibold">Click to upload</span> or drag &amp; drop
              </p>
              <p className="font-sans text-xs text-gray-400">PNG, JPG, WEBP — up to 10 images</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileInput}
                className="hidden"
              />
            </div>

            {files.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {files.map((file, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden border border-grey-border bg-grey-bg aspect-square">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <i className="ri-close-line text-white text-xs"></i>
                    </button>
                  </div>
                ))}
                {files.length < 10 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-grey-border flex flex-col items-center justify-center hover:border-orange-brand/60 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-gray-400 text-xl"></i>
                    <span className="text-xs font-sans text-gray-400 mt-1">Add more</span>
                  </button>
                )}
              </div>
            )}
            <p className="text-xs font-sans text-gray-400 mt-3">{files.length}/10 photos added</p>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <div>
            <h4 className="font-heading font-bold text-navy text-base mb-1">Review &amp; Submit</h4>
            <p className="font-sans text-gray-400 text-sm mb-5">Please confirm your details before submitting.</p>
            <div className="space-y-3">
              {[
                { label: 'Services', value: form.services.join(', '), icon: 'ri-tools-line' },
                { label: 'Name', value: form.fullName, icon: 'ri-user-line' },
                { label: 'Phone', value: form.phone, icon: 'ri-phone-line' },
                { label: 'Email', value: form.email, icon: 'ri-mail-line' },
                { label: 'Address', value: `${form.address}, ${form.suburb} ${form.state} ${form.postcode}`, icon: 'ri-map-pin-line' },
                { label: 'Photos', value: files.length > 0 ? `${files.length} photo(s) attached` : 'No photos', icon: 'ri-image-line' },
              ].map((item) => (
                <div key={item.label} className="flex items-start space-x-3 bg-grey-bg rounded-lg px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-navy-50 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-navy text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-sans font-semibold text-gray-400 uppercase tracking-wide">{item.label}</p>
                    <p className="text-sm font-sans text-navy mt-0.5 break-words">{item.value || '—'}</p>
                  </div>
                </div>
              ))}
            </div>
            {error && (
              <p className="text-red-500 text-xs font-sans mt-3 text-center">{error}</p>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-grey-border">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center space-x-2 px-5 py-2.5 border border-grey-border text-navy font-heading font-semibold text-sm rounded-lg hover:bg-grey-bg transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              <span>Back</span>
            </button>
          ) : (
            <div />
          )}

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => { if (canNext()) setStep((s) => s + 1); }}
              disabled={!canNext()}
              className="flex items-center space-x-2 px-6 py-2.5 bg-orange-brand text-white font-heading font-semibold text-sm rounded-lg hover:bg-orange-dark transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-sm"
            >
              <span>Continue</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-8 py-2.5 bg-orange-brand text-white font-heading font-bold text-sm rounded-lg hover:bg-orange-dark transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-md"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-2-line"></i>
                  <span>Request Quote</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
